/**
 * A tech's tickets not closed, from every day (`GET /api/v1/tickets`; ADR
 * 0183): the open-tickets tray's (v0.9.0's P5), the Open tickets tile's
 * (P2b) and the drawer's tab (ADR 0190), from one query, so they always
 * agree.
 *
 * It also gives the tickets the tech is only a secondary resource on
 * (`secondary_only`; ADR 0192): the tray's lower section. They aren't the
 * tech's, so neither the tile nor the tab counts them (`ownTickets`).
 */
import { useQuery } from "@tanstack/react-query";

import { retryUnlessRefused } from "../../api/client";
import type { TrayTicket } from "../../api/types";
import { useSession } from "../../app/session-context";

/** How often they're read again: as the day is (section 11.3). */
const TICKETS_POLL_MS = 30_000;

/** Whose: the viewed tech's; null for the signed-in user's own. */
export function useTrayTickets(userId: string | null) {
  const { api } = useSession();
  return useQuery({
    queryKey: ["tickets", userId ?? "me"],
    queryFn: () => api.listTickets(userId),
    refetchInterval: TICKETS_POLL_MS,
    retry: retryUnlessRefused,
  });
}

/** The tech's own: every one but those they're only a secondary resource on (ADR 0192). */
export function ownTickets(tickets: readonly TrayTicket[]): TrayTicket[] {
  return tickets.filter((t) => !t.secondary_only);
}

/** Those they're only a secondary resource on: the tray's lower section (ADR 0192). */
export function secondaryTickets(tickets: readonly TrayTicket[]): TrayTicket[] {
  return tickets.filter((t) => t.secondary_only);
}

/** When the oldest one's age counts from; null with none. */
export function oldestOf(tickets: readonly TrayTicket[]): string | null {
  return tickets.reduce<string | null>(
    (first, t) =>
      t.age_from !== null && (first === null || Date.parse(t.age_from) < Date.parse(first)) ? t.age_from : first,
    null,
  );
}

/** The drawer's width on a wide screen with no Closed column to cover, or past 560 px. */
export const DRAWER_PX = 480;

/**
 * The open-tickets drawer's width on a wide screen (ADR 0190), from how far
 * the board's Closed column's left edge is from the window's right edge:
 * that reach, so it covers Closed; never under 320 px, which a row needs;
 * and past about 560 px (a very wide window), 480 px rather than more.
 */
export function drawerWidth(reach: number): number {
  const width = Math.round(reach);
  if (width < 320) {
    return 320;
  }
  return width > 560 ? DRAWER_PX : width;
}
